/**
 * Line icons used by the section eyebrows and the architecture cards.
 *
 * Inline SVG rather than an icon package: this surface is prerendered to static
 * HTML, and inlining keeps the snapshot self-contained — no icon font to load,
 * nothing that renders as a blank box before JS runs. They inherit `color`, so
 * the eyebrow rule sets them all at once.
 */

const BASE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function GearIcon() {
  return (
    <svg {...BASE}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

export function RocketIcon() {
  return (
    <svg {...BASE}>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09Z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2Z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

export function CubeIcon() {
  return (
    <svg {...BASE}>
      <path d="m21 16-9 5-9-5V8l9-5 9 5Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  );
}

export function BulbIcon() {
  return (
    <svg {...BASE}>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
    </svg>
  );
}

export function PulseIcon() {
  return (
    <svg {...BASE}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}

export function ClockIcon() {
  return (
    <svg {...BASE}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function BarsIcon() {
  return (
    <svg {...BASE}>
      <path d="M3 3v18h18" />
      <path d="M7 15v3" />
      <path d="M12 10v8" />
      <path d="M17 6v12" />
    </svg>
  );
}

export function ArrowUpRightIcon() {
  return (
    <svg {...BASE}>
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

export function WalletIcon() {
  return (
    <svg {...BASE}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
      <circle cx="17" cy="14.5" r="1" />
    </svg>
  );
}

export function HandCoinIcon() {
  return (
    <svg {...BASE}>
      <circle cx="16" cy="6" r="3" />
      <path d="M2 15c1.5-1 3-1 4.5 0l2.2 1.5h3.1c1 0 1.6 1.2.9 1.9l-.3.3" />
      <path d="M22 13c-2 2-4.5 4-7 5.5-1.4.8-3 .8-4.4 0L2 14" />
    </svg>
  );
}

export function ShieldCheckIcon() {
  return (
    <svg {...BASE}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function HeadIcon() {
  return (
    <svg {...BASE}>
      <path d="M13.5 21H8v-3.2A7.5 7.5 0 0 1 5 12a7 7 0 0 1 13.9-1.2l1.7 4a.8.8 0 0 1-.7 1.1h-1.7v2.4a1.7 1.7 0 0 1-1.7 1.7h-1" />
      <circle cx="11.5" cy="10.5" r="1.6" />
      <path d="M11.5 12.1V15" />
      <path d="m10.2 9.3-1.5-1.1" />
    </svg>
  );
}

export function KeyIcon() {
  return (
    <svg {...BASE}>
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="m10.7 12.3 8.3-8.3" />
      <path d="m16 7 2 2" />
      <path d="m19 4 2 2" />
    </svg>
  );
}

export function CodeIcon() {
  return (
    <svg {...BASE}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m9 10-2 2 2 2" />
      <path d="m14 10 2 2-2 2" />
    </svg>
  );
}

export function CoinsIcon() {
  return (
    <svg {...BASE}>
      <circle cx="9" cy="9" r="6" />
      <path d="M15.5 4.2a6 6 0 0 1 0 15.6" />
      <path d="M12 15.5a6 6 0 0 0 3.5 4.3" />
    </svg>
  );
}

export function LinkIcon() {
  return (
    <svg {...BASE}>
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
    </svg>
  );
}

export function TrendIcon() {
  return (
    <svg {...BASE}>
      <path d="M22 7 13.5 15.5 8.5 10.5 2 17" />
      <path d="M16 7h6v6" />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg {...BASE}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

/**
 * Social marks. Solid rather than stroked — these are logos, and outlining
 * them makes them read as generic glyphs. Each is paired with a visually
 * hidden label at the call site, so the icon itself stays `aria-hidden`.
 */
const SOLID = { viewBox: '0 0 24 24', fill: 'currentColor', 'aria-hidden': true } as const;

export function XIcon() {
  return (
    <svg {...SOLID}>
      <path d="M17.5 3h3.2l-7 8 8.2 10h-6.4l-5-6.1L4.7 21H1.5l7.5-8.6L1.2 3h6.6l4.5 5.6L17.5 3Zm-1.1 16h1.8L7.7 4.9H5.8L16.4 19Z" />
    </svg>
  );
}

export function LinkedInIcon() {
  return (
    <svg {...SOLID}>
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm6.5 0h3.8v1.7h.05a4.2 4.2 0 0 1 3.75-2c4 0 4.75 2.6 4.75 6V21h-4v-5.4c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V21h-4V9Z" />
    </svg>
  );
}

export function YouTubeIcon() {
  return (
    <svg {...SOLID}>
      <path d="M23 12s0-3.4-.4-5a2.9 2.9 0 0 0-2-2C18.7 4.5 12 4.5 12 4.5s-6.7 0-8.6.5a2.9 2.9 0 0 0-2 2C1 8.6 1 12 1 12s0 3.4.4 5a2.9 2.9 0 0 0 2 2c1.9.5 8.6.5 8.6.5s6.7 0 8.6-.5a2.9 2.9 0 0 0 2-2c.4-1.6.4-5 .4-5ZM9.8 15.5v-7l5.8 3.5-5.8 3.5Z" />
    </svg>
  );
}

/** Filled triangle for the video poster's play control. */
export function PlayIcon() {
  return (
    <svg {...SOLID}>
      <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.3-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z" />
    </svg>
  );
}

export function CheckBadgeIcon() {
  return (
    <svg {...BASE}>
      <path d="m12 2 2.4 1.8 3-.1 1 2.8 2.5 1.6-1 2.9 1 2.9-2.5 1.6-1 2.8-3-.1L12 22l-2.4-1.8-3 .1-1-2.8L3.1 15.9l1-2.9-1-2.9 2.5-1.6 1-2.8 3 .1Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
